import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { toast } from "@/components/ui/sonner";
import {
  companyBiometricDeviceDisplayName,
  deleteCompanyBiometricDevice,
  listCompanyBiometricDeviceRecords,
  syncCompanyBiometricDevicesFromApi,
  type CompanyBiometricDeviceRecord,
} from "@/lib/admin/companyBiometricDevicesStore";
import { companyBiometricDeviceFormErrorMessage } from "@/lib/admin/companyBiometricDeviceForm";
import { formatBiometricDeviceProtocol } from "@/lib/admin/biometricDeviceForm";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { useGridPagination } from "@/hooks/useGridPagination";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import {
  Building2,
  CalendarIcon,
  Edit,
  Eye,
  Filter,
  Fingerprint,
  LayoutGrid,
  List,
  Network,
  Plus,
  Search,
  Server,
  Trash2,
} from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;
type SortBy =
  | "latest"
  | "oldest"
  | "name_asc"
  | "name_desc"
  | "ip_asc"
  | "ip_desc"
  | "status_active_first"
  | "status_inactive_first";

const CompanyBiometricDevices = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "company_biometric_device_can_view");
  const canCreate = can(user, "company_biometric_device_can_create");
  const canUpdate = can(user, "company_biometric_device_can_update");
  const canDelete = can(user, "company_biometric_device_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);
  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const rows = useMemo(() => listCompanyBiometricDeviceRecords(), [version]);

  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("latest");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [quickDate, setQuickDate] = useState<"all" | "today" | "week" | "month">("all");
  const [filterName, setFilterName] = useState("");
  const [filterIp, setFilterIp] = useState("");
  const [filterCompany, setFilterCompany] = useState("");
  const [onlyActive, setOnlyActive] = useState(false);
  const [onlyInactive, setOnlyInactive] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [deleteTarget, setDeleteTarget] = useState<CompanyBiometricDeviceRecord | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setIsForbidden(false);
      try {
        await syncCompanyBiometricDevicesFromApi();
        if (mounted) refresh();
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 403) {
          if (mounted) setIsForbidden(true);
          return;
        }
        console.error("[CompanyBiometricDevices] Load failed", e);
        if (mounted) {
          toast.error(companyBiometricDeviceFormErrorMessage(e) || "Could not fetch company devices. Showing cached data.");
        }
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nameQ = filterName.trim().toLowerCase();
    const ipQ = filterIp.trim().toLowerCase();
    const companyQ = filterCompany.trim().toLowerCase();

    return rows.filter((r) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(r.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(r.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      if (onlyActive && !r.isActive) return false;
      if (onlyInactive && r.isActive) return false;
      if (nameQ) {
        const label = companyBiometricDeviceDisplayName(r).toLowerCase();
        if (!label.includes(nameQ) && !(r.serialNumber ?? "").toLowerCase().includes(nameQ)) return false;
      }
      if (ipQ && !r.ipAddress.toLowerCase().includes(ipQ)) return false;
      if (companyQ && !(r.companyName ?? "").toLowerCase().includes(companyQ)) return false;
      if (q) {
        const blob = [
          r.serialNumber,
          r.ipAddress,
          r.location,
          r.companyName,
          r.biometricModel,
          r.biometricDriver,
        ].map((s) => (s ?? "").toLowerCase());
        if (!blob.some((x) => x.includes(q))) return false;
      }
      return true;
    });
  }, [rows, query, filterCreatedDate, quickDate, filterName, filterIp, filterCompany, onlyActive, onlyInactive]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return companyBiometricDeviceDisplayName(b).localeCompare(companyBiometricDeviceDisplayName(a));
        case "name_asc":
          return companyBiometricDeviceDisplayName(a).localeCompare(companyBiometricDeviceDisplayName(b));
        case "ip_desc":
          return b.ipAddress.localeCompare(a.ipAddress);
        case "ip_asc":
          return a.ipAddress.localeCompare(b.ipAddress);
        case "status_active_first": {
          const sa = a.isActive ? 0 : 1;
          const sb = b.isActive ? 0 : 1;
          if (sa !== sb) return sa - sb;
          return companyBiometricDeviceDisplayName(a).localeCompare(companyBiometricDeviceDisplayName(b));
        }
        case "status_inactive_first": {
          const sa = a.isActive ? 1 : 0;
          const sb = b.isActive ? 1 : 0;
          if (sa !== sb) return sa - sb;
          return companyBiometricDeviceDisplayName(a).localeCompare(companyBiometricDeviceDisplayName(b));
        }
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount =
    (filterCreatedDate ? 1 : 0) +
    (filterName.trim() ? 1 : 0) +
    (filterIp.trim() ? 1 : 0) +
    (filterCompany.trim() ? 1 : 0) +
    (onlyActive ? 1 : 0) +
    (onlyInactive ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterName("");
    setFilterIp("");
    setFilterCompany("");
    setOnlyActive(false);
    setOnlyInactive(false);
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterName}|${filterIp}|${filterCompany}|${onlyActive ? 1 : 0}|${onlyInactive ? 1 : 0}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const runDelete = async () => {
    if (!deleteTarget || isDeleting) return;
    setIsDeleting(true);
    try {
      const r = await deleteCompanyBiometricDevice(deleteTarget.id);
      toast.success(r.message);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      console.error("[CompanyBiometricDevices] Delete failed", e);
      toast.error(companyBiometricDeviceFormErrorMessage(e));
    } finally {
      setIsDeleting(false);
    }
  };

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Company biometric devices access required"
        message="You don’t have permission to view company biometric devices. Ask an administrator to grant company_biometric_device_can_view_any."
      />
    );
  }

  return (
    <Layout title="Company biometric devices">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Company biometric devices</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Hardware linked to companies (IP, port, location).{" "}
              <Link to="/biometric-devices" className="text-primary hover:underline font-medium">
                Device catalog
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" asChild>
              <Link to="/company-biometric-devices/add">
                <Plus className="w-4 h-4" /> Add company device
              </Link>
            </Button>
          ) : null}
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, IP, company, model, or date…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                  {appliedFiltersCount > 0 ? (
                    <span className="ml-0.5 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      {appliedFiltersCount}
                    </span>
                  ) : null}
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[420px] overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Filter company devices</SheetTitle>
                  <SheetDescription>Refine by model, IP, company, status, and created date.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-5">
                  <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
                    <p className="text-sm font-medium text-foreground">Device</p>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Model / serial contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">IP contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterIp}
                        onChange={(e) => setFilterIp(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Company contains</Label>
                      <Input
                        className="glass-input h-10"
                        value={filterCompany}
                        onChange={(e) => setFilterCompany(e.target.value)}
                        autoComplete="off"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-muted-foreground">Created date</Label>
                      <Popover modal>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            className={cn(
                              "glass-input h-10 w-full justify-start text-left font-normal",
                              !filterCreatedDate && "text-muted-foreground",
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                            {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                          <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                          <div className="flex gap-2 border-t p-2">
                            <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                              Clear
                            </Button>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Active only</Label>
                      <p className="text-xs text-muted-foreground">Status = active</p>
                    </div>
                    <Checkbox
                      checked={onlyActive}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyActive(next);
                        if (next) setOnlyInactive(false);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-border/40 bg-muted/15 px-3 py-2">
                    <div className="space-y-0.5">
                      <Label className="text-sm">Inactive only</Label>
                      <p className="text-xs text-muted-foreground">Status = inactive</p>
                    </div>
                    <Checkbox
                      checked={onlyInactive}
                      onCheckedChange={(v) => {
                        const next = Boolean(v);
                        setOnlyInactive(next);
                        if (next) setOnlyActive(false);
                      }}
                    />
                  </div>
                </div>
                <SheetFooter className="mt-6 gap-2">
                  <Button type="button" variant="glass" className="w-full" onClick={() => resetSheetFilters()}>
                    Reset
                  </Button>
                  <SheetClose asChild>
                    <Button type="button" variant="premium" className="w-full">
                      Apply filters
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <div className="overflow-x-auto flex-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today", mobile: "Today" },
                    { id: "week" as const, label: "Week", mobile: "7D" },
                    { id: "month" as const, label: "Month", mobile: "30D" },
                    { id: "all" as const, label: "All", mobile: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    <span className="sm:hidden">{opt.mobile}</span>
                    <span className="hidden sm:inline">{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
              <button
                type="button"
                onClick={() => setViewMode("grid")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="Grid view"
              >
                <LayoutGrid className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode("list")}
                className={cn(
                  "h-full px-3 text-sm transition-colors",
                  viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                )}
                aria-label="List view"
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 md:hidden">
            <GridPerPageSelect
              id="company-biometric-devices-page-size-mobile"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="w-[180px] shrink-0">
              <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
                <SelectTrigger className="glass-input h-10 w-full">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Newest first</SelectItem>
                  <SelectItem value="oldest">Oldest first</SelectItem>
                  <SelectItem value="name_asc">Model: A to Z</SelectItem>
                  <SelectItem value="name_desc">Model: Z to A</SelectItem>
                  <SelectItem value="ip_asc">IP: A to Z</SelectItem>
                  <SelectItem value="ip_desc">IP: Z to A</SelectItem>
                  <SelectItem value="status_active_first">Active first</SelectItem>
                  <SelectItem value="status_inactive_first">Inactive first</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="hidden md:flex items-center justify-between gap-3">
            <GridPerPageSelect
              id="company-biometric-devices-page-size-desktop"
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              options={PAGE_SIZE_OPTIONS}
            />
            <div className="flex items-center gap-3 justify-end min-w-0">
              <div className="w-[200px] shrink-0">
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortBy)}>
                  <SelectTrigger className="glass-input h-10 w-full">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Newest first</SelectItem>
                    <SelectItem value="oldest">Oldest first</SelectItem>
                    <SelectItem value="name_asc">Model: A to Z</SelectItem>
                    <SelectItem value="name_desc">Model: Z to A</SelectItem>
                    <SelectItem value="ip_asc">IP: A to Z</SelectItem>
                    <SelectItem value="ip_desc">IP: Z to A</SelectItem>
                    <SelectItem value="status_active_first">Active first</SelectItem>
                    <SelectItem value="status_inactive_first">Inactive first</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background whitespace-nowrap">
                {(
                  [
                    { id: "today" as const, label: "Today" },
                    { id: "week" as const, label: "Week" },
                    { id: "month" as const, label: "Month" },
                    { id: "all" as const, label: "All" },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setQuickDate(opt.id)}
                    className={cn(
                      "h-full px-3 text-sm transition-colors",
                      quickDate === opt.id
                        ? "bg-primary/10 text-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0">
                <button
                  type="button"
                  onClick={() => setViewMode("grid")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "grid" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="Grid view"
                >
                  <LayoutGrid className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("list")}
                  className={cn(
                    "h-full px-3 text-sm transition-colors",
                    viewMode === "list" ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50",
                  )}
                  aria-label="List view"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {rows.length === 0 ? (
              canCreate ? (
                <>
                  No company devices yet.{" "}
                  <Link to="/company-biometric-devices/add" className="text-primary font-medium hover:underline">
                    Add a company device
                  </Link>
                  .
                </>
              ) : (
                "No company devices yet."
              )
            ) : (
              "No devices match your search or filters."
            )}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {paginated.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}>
                <CatalogEntityCard
                  title={companyBiometricDeviceDisplayName(r)}
                  titleIcon={Fingerprint}
                  statusBadge={
                    r.isActive
                      ? { label: "Active", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/25" }
                      : { label: "Inactive", className: "bg-muted/50 text-muted-foreground border-border/60" }
                  }
                  primaryMeta={{
                    label: "IP · Port",
                    value: `${r.ipAddress}:${r.port}`,
                    icon: Server,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: r.companyName ? "Company" : "Model",
                    value: r.companyName ?? r.biometricModel ?? "—",
                    icon: r.companyName ? Building2 : Network,
                    iconClassName: "text-amber-600",
                  }}
                  footer={`Last updated: ${formatCatalogAuditDate(r.updatedAt)}`}
                  onView={canView ? () => navigate(`/company-biometric-devices/${r.id}`) : undefined}
                  onEdit={canUpdate ? () => navigate(`/company-biometric-devices/${r.id}/edit`) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(r) : undefined}
                />
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 && viewMode === "list" ? (
          <div className="space-y-3">
            {paginated.map((r, i) => (
              <motion.div
                key={r.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-14 w-14 rounded-md bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <Fingerprint className="w-7 h-7 text-muted-foreground/50" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{companyBiometricDeviceDisplayName(r)}</h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.ipAddress}:{r.port}
                      {r.companyName ? ` · ${r.companyName}` : ""}
                      {r.biometricModel ? ` · ${r.biometricModel}` : ""}
                      {r.biometricProtocol ? ` · ${formatBiometricDeviceProtocol(r.biometricProtocol)}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5" asChild>
                        <Link to={`/company-biometric-devices/${r.id}`}>
                          <Eye className="h-3.5 w-3.5" /> View
                        </Link>
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5" asChild>
                        <Link to={`/company-biometric-devices/${r.id}/edit`}>
                          <Edit className="h-3.5 w-3.5" /> Edit
                        </Link>
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button variant="ghost" size="sm" className="gap-1.5 text-destructive" onClick={() => setDeleteTarget(r)}>
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : null}

        {sorted.length > 0 ? (
          <GridPaginationFooter
            totalCount={totalCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        ) : null}

        <ConfirmAlertDialog
          open={Boolean(deleteTarget)}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this company device?"
          description={
            deleteTarget ? (
              <>
                Are you sure you want to delete{" "}
                <strong className="text-foreground font-semibold">{companyBiometricDeviceDisplayName(deleteTarget)}</strong>? This cannot
                be undone.
              </>
            ) : null
          }
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void runDelete()}
        />
      </div>
    </Layout>
  );
};

export default CompanyBiometricDevices;
